import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import CurrentAssistantInfo from './CurrentAssistantInfo';
import CurriculumTreeView from './CurriculumTreeView';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'uploaded',
})(({ theme, uploaded }) => ({
  border: `2px dashed ${uploaded ? theme.palette.success.main : theme.palette.grey[300]}`,
  backgroundColor: uploaded ? theme.palette.success[50] : theme.palette.grey[50],
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: uploaded ? theme.palette.success.main : theme.palette.primary.main,
    backgroundColor: uploaded ? theme.palette.success[50] : theme.palette.primary[50],
  },
}));

const CurriculumAnalysisStep = ({
  qualificationId,
  onUpload,
  loading,
  uploadedFileInfo,
  eloFileInfo,
  clientBriefFileInfo,
  curriculumHasExitLevelOutcomes,
  onCurriculumExitLevelOutcomesChange,
  hasExitLevelOutcomes,
  onExitLevelOutcomesChange,
  onProceedToNextStep,
  setJsonTreeModalOpen,
  onRegenerateCurriculum,
  extractNonce = 0
}) => {
  const [curriculumFile, setCurriculumFile] = useState(null);
  const [hasExtractedCurriculum, setHasExtractedCurriculum] = useState(null);
  const [eloFile, setEloFile] = useState(null);
  const [clientBriefFile, setClientBriefFile] = useState(null);
  const [curriculumFileName, setCurriculumFileName] = useState('');
  const [eloFileName, setEloFileName] = useState('');
  const [clientBriefFileName, setClientBriefFileName] = useState('');

  const clientBriefUploaded = !!clientBriefFileInfo;

  const handleCurriculumFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCurriculumFile(file);
      setCurriculumFileName(file.name);
      console.log('[CurriculumDoc] file:selected', {
        qualificationId,
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
        type: file.type,
      });
    }
  };

  const handleEloFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setEloFile(file);
      setEloFileName(file.name);
    }
  };

  const handleClientBriefSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setClientBriefFile(file);
      setClientBriefFileName(file.name);
    }
  };

  const handleCurriculumUpload = async () => {
    if (curriculumFile) {
      const selectedName = curriculumFile.name;
      console.log('[CurriculumDoc] upload:start', {
        qualificationId,
        documentType: 'curriculum',
        fileName: selectedName,
        fileSize: curriculumFile.size,
      });
      const formData = new FormData();
      formData.append('file', curriculumFile);
      formData.append('documentType', 'curriculum');
      formData.append('hasExitLevelOutcomes', curriculumHasExitLevelOutcomes);
      await onUpload(formData, { selectedFileName: selectedName });
      setCurriculumFile(null);
      setCurriculumFileName('');
    }
  };

  const handleEloUpload = async () => {
    if (eloFile) {
      const formData = new FormData();
      formData.append('file', eloFile);
      formData.append('documentType', 'elo');
      formData.append('hasExitLevelOutcomes', hasExitLevelOutcomes);
      await onUpload(formData);
      setEloFile(null);
      setEloFileName('');
    }
  };

  const handleClientBriefUpload = async () => {
    if (clientBriefFile) {
      const formData = new FormData();
      formData.append('file', clientBriefFile);
      formData.append('documentType', 'client_brief');
      await onUpload(formData);
      setClientBriefFile(null);
      setClientBriefFileName('');
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          Curriculum Analysis
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload your curriculum documents and review the extracted structure
        </Typography>
      </Box>

      {/* AI Assistant Info */}
      <Box sx={{ mb: 4 }}>
        <CurrentAssistantInfo
          qualificationId={qualificationId}
          step="step_1"
          onEditAssistant={() => {}}
        />
      </Box>

      {/* Document Upload Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Curriculum Document Upload */}
        <Grid size={{ xs: 12, md: 6 }}>
          <UploadCard uploaded={!!uploadedFileInfo}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Curriculum Document
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Upload your main curriculum document (PDF/DOCX)
              </Typography>
              {clientBriefUploaded && (
                <Alert severity="info" sx={{ mb: 2 }}>Disabled because a Client Brief has been uploaded.</Alert>
              )}
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={curriculumHasExitLevelOutcomes}
                    onChange={(e) => onCurriculumExitLevelOutcomesChange(e.target.checked)}
                    color="primary"
                    disabled={clientBriefUploaded}
                  />
                }
                label="Document contains exit level outcomes"
                sx={{ mb: 2 }}
              />
              
              {uploadedFileInfo ? (
                <Box>
                  <Chip 
                    icon={<CheckCircleIcon />} 
                    label="Uploaded" 
                    color="success" 
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {uploadedFileInfo.fileName}
                  </Typography>
                  {onRegenerateCurriculum && hasExtractedCurriculum === false && (
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={onRegenerateCurriculum}
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      Generate
                    </Button>
                  )}
                </Box>
              ) : (
                <Box>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 2 }}
                    disabled={clientBriefUploaded}
                  >
                    Choose Curriculum Document
                    <VisuallyHiddenInput
                      type="file"
                      accept=".pdf,.docx,.txt"
                      onChange={handleCurriculumFileSelect}
                    />
                  </Button>
                  {curriculumFileName && (
                    <Typography variant="body2" color="text.secondary">
                      Selected: {curriculumFileName}
                    </Typography>
                  )}
                  {curriculumFile && (
                    <Button
                      variant="contained"
                      onClick={handleCurriculumUpload}
                      disabled={loading || clientBriefUploaded}
                      sx={{ mt: 1 }}
                    >
                      {loading ? <CircularProgress size={20} /> : 'Upload'}
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </UploadCard>
        </Grid>

        {/* ELO Document Upload */}
        <Grid size={{ xs: 12, md: 6 }}>
          <UploadCard uploaded={!!eloFileInfo}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <SchoolIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Exit Level Outcomes Document
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Upload document containing exit level outcomes (optional)
              </Typography>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasExitLevelOutcomes}
                    onChange={(e) => onExitLevelOutcomesChange(e.target.checked)}
                    color="secondary"
                    disabled={clientBriefUploaded}
                  />
                }
                label="Document contains exit level outcomes"
                sx={{ mb: 2 }}
              />
              
              {eloFileInfo ? (
                <Box>
                  <Chip 
                    icon={<CheckCircleIcon />} 
                    label="Uploaded" 
                    color="success" 
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {eloFileInfo.fileName}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    color="secondary"
                    sx={{ mb: 2 }}
                    disabled={clientBriefUploaded}
                  >
                    Choose ELO Document
                    <VisuallyHiddenInput
                      type="file"
                      accept=".pdf,.docx"
                      onChange={handleEloFileSelect}
                    />
                  </Button>
                  {eloFileName && (
                    <Typography variant="body2" color="text.secondary">
                      Selected: {eloFileName}
                    </Typography>
                  )}
                  {eloFile && (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleEloUpload}
                      disabled={loading || clientBriefUploaded}
                      sx={{ mt: 1 }}
                    >
                      {loading ? <CircularProgress size={20} /> : 'Upload'}
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </UploadCard>
        </Grid>
      </Grid>

      {/* Client Brief Upload Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Client Brief (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a client brief document. When uploaded, curriculum and ELO uploads are disabled.
        </Typography>
        {clientBriefFileInfo ? (
          <Chip icon={<CheckCircleIcon />} label={`Uploaded: ${clientBriefFileInfo.fileName}`} color="success" />
        ) : (
          <Box>
            <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} sx={{ mr: 2 }}>
              Choose Client Brief
              <VisuallyHiddenInput type="file" accept=".pdf,.docx" onChange={handleClientBriefSelect} />
            </Button>
            {clientBriefFileName && (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'inline-block', mr: 2 }}>
                Selected: {clientBriefFileName}
              </Typography>
            )}
            {clientBriefFile && (
              <Button variant="contained" onClick={handleClientBriefUpload} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Upload Client Brief'}
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* Full-screen loader overlay during upload */}
      {loading && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <CircularProgress size={72} sx={{ color: '#fff', mb: 2 }} />
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800 }}>Processing curriculum...</Typography>
        </Box>
      )}

      {/* Curriculum Tree View Section */}
      {uploadedFileInfo && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h6">
              Curriculum Structure
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={() => setJsonTreeModalOpen && setJsonTreeModalOpen(true)}
                size="small"
              >
                View JSON Tree
              </Button>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <CurriculumTreeView
            qualificationId={qualificationId}
            extractNonce={extractNonce}
            onExtractionStateChange={({ extracted }) => setHasExtractedCurriculum(Boolean(extracted))}
          />
        </Paper>
      )}

      {/* Action Buttons */}
      {uploadedFileInfo && (
        <Box display="flex" gap={2} justifyContent="flex-end" flexWrap="wrap">
          <Button
            variant="contained"
            color="success"
            onClick={onProceedToNextStep}
            disabled={loading}
            size="large"
          >
            Approve & Continue to Overview
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CurriculumAnalysisStep;

